import React from 'react';
import { Box, Typography, Button, TextField, Chip } from '@mui/material';
import { searchStudentsAndClasses } from './scopeUtils';

export function PiiResolutionCard({
    pendingPiiResolution,
    inputValue,
    piiSearchQueries,
    setPiiSearchQueries,
    onReplace,
    onDismiss,
    schoolStudents,
    schoolClasses,
}) {
    if (!pendingPiiResolution || !Object.keys(pendingPiiResolution.entities).some((key) => inputValue.includes(key))) {
        return null;
    }

    return (
        <Box
            className="pii-resolution-card"
            sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255, 193, 7, 0.12)',
                border: '1px solid rgba(255, 193, 7, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: 'min(40vh, 320px)',
                overflowY: 'auto',
                flexShrink: 1,
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 0.5,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Replace names with scopes
                </Typography>
                <Button size="small" onClick={onDismiss} sx={{ minWidth: 0, textTransform: 'none' }}>
                    Dismiss
                </Button>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                We detected names in your message. Pick a matching student or class to re-run your question safely.
            </Typography>
            {Object.entries(pendingPiiResolution.entities)
                .filter(([placeholderKey]) => inputValue.includes(placeholderKey))
                .map(([placeholderKey, entity]) => {
                    const searchValue = entity?.value ?? placeholderKey;
                    const manualQuery = piiSearchQueries[placeholderKey] ?? '';
                    const query = manualQuery.trim() || searchValue;
                    const matches = searchStudentsAndClasses(query, schoolStudents, schoolClasses);
                    return (
                        <Box key={placeholderKey} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                &quot;{searchValue}&quot; {entity?.type_key ? `(${entity.type_key})` : ''}
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Search students and classes…"
                                value={piiSearchQueries[placeholderKey] ?? ''}
                                onChange={(e) =>
                                    setPiiSearchQueries((prev) => ({ ...prev, [placeholderKey]: e.target.value }))
                                }
                                sx={{
                                    '& .MuiInputBase-root': { bgcolor: 'background.paper', borderRadius: 1 },
                                    '& .MuiInputBase-input': { py: 0.5, fontSize: '0.875rem' },
                                }}
                                inputProps={{ 'aria-label': `Search for ${searchValue}` }}
                            />
                            {matches.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {matches.map((scope) => (
                                        <Chip
                                            key={`${scope.type}-${scope.id}`}
                                            size="small"
                                            label={scope.label}
                                            onClick={() => onReplace(placeholderKey, scope)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: 'background.paper',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : query.trim() ? (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                                >
                                    No matches for &quot;{query}&quot;. Try a different search.
                                </Typography>
                            ) : null}
                        </Box>
                    );
                })}
        </Box>
    );
}
